import { type CheckoutProduct } from '@/modules/checkout/types';
import { type CurrencyCode } from '@/modules/currencies/constants';
import { type Offer } from '@/modules/offers/buyers/types';
import { productPictureToPictureFormat } from '@/modules/pictures/helpers';
import { type ProductPicture } from '@/modules/product/types';

type CheckoutSource = Pick<
  Offer,
  | 'product_id'
  | 'product_slug'
  | 'product_country_code'
  | 'product_description'
  | 'variant_id'
  | 'checkout'
>;

export function mapOfferToCheckoutProduct(
  offer: CheckoutSource
): CheckoutProduct | undefined {
  const { checkout } = offer;

  if (!checkout) {
    return undefined;
  }

  const { pricing, seller, seller_reviews, seller_activity, pictures } =
    checkout;

  const shippingCost =
    Number(pricing.original_price.price_breakdown.shipping?.amount) > 0
      ? pricing.original_price.price_breakdown.shipping?.amount
      : undefined;

  const checkoutProduct: CheckoutProduct = {
    id: offer.product_id,
    slug: offer.product_slug,
    countryCode: offer.product_country_code,
    price: {
      priceAmount: pricing.original_price.total_price,
      nationalShippingCost:
        pricing.national_shipping_cost?.total_price ?? shippingCost,
      internationalShippingCost:
        pricing.international_shipping_cost?.total_price,
      discountedPriceAmount: pricing.discounted_price?.total_price,
      currencyName: pricing.currency as CurrencyCode,
    },
    seller: {
      id: seller.id,
      username: seller.username,
      pictureUrl: Object.values(seller.picture ?? {})[0],
      picture: Object.entries(seller.picture ?? {}).map(([size, url]) => ({
        width: Number(size),
        height: Number(size),
        url,
      })),
      initials: seller.initials,
      verified: seller.verified,
      firstName: seller.first_name,
      lastName: seller.last_name,
      reviewsTotal: seller_reviews?.reviews_total,
      reviewsRating: seller_reviews?.reviews_rating,
      itemsSold: seller_activity?.items_sold,
      lastSeen: seller_activity?.last_seen,
    },
    pictures: (pictures ?? []).map((pic) =>
      productPictureToPictureFormat(pic as ProductPicture)
    ),
    description: offer.product_description,
    brandName: checkout.brand_name,
    productType: checkout.product_type || '',
  };

  const size = offer.variant_id ? offer.variant_id.toString() : undefined;

  if (size) {
    checkoutProduct.size = { id: size };
  }

  return checkoutProduct;
}
